import { useEffect, useState } from 'react';
import { apiGet } from '@/lib/api';
import { FLIGHT_TYPES, FLIGHT_TYPE_MAP } from '@/lib/flightTypes';
import { PageHeader, Card, Spinner, ErrorNote, inputClass } from '@/components/ui';

interface TaxReport {
  totalFlights: number;
  totalHours: number;
  byType: Record<string, { count: number; hours: number }>;
  deductibleFlights: number;
  deductibleHours: number;
  deductiblePercentage: number;
  issues: Array<{ type: 'warning' | 'error'; code: string; message: string }>;
}

function Stat({ label, value, accent = 'text-white', hint }: { label: string; value: string | number; accent?: string; hint?: string }) {
  return (
    <Card className="p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-2 text-3xl font-bold tabular-nums ${accent}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </Card>
  );
}

export default function Analytics() {
  const yearStart = `${new Date().getFullYear()}-01-01`;
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(yearStart);
  const [end, setEnd] = useState(today);
  const [report, setReport] = useState<TaxReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  function run() {
    setLoading(true);
    setError('');
    apiGet<TaxReport>(`/api/analytics/tax-report?start=${start}T00:00:00Z&end=${end}T23:59:59Z`)
      .then(setReport)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(run, []); // eslint-disable-line react-hooks/exhaustive-deps

  const csvHref = `/api/analytics/tax-report/csv?start=${start}T00:00:00Z&end=${end}T23:59:59Z`;
  const maxHours = report ? Math.max(1, ...FLIGHT_TYPES.map((t) => report.byType[t.code]?.hours || 0)) : 1;

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6 lg:p-8">
      <PageHeader
        title="Tax Analytics"
        subtitle="Deductibility summary and compliance for your N313G flights."
        actions={<a href={csvHref} className="btn-ghost text-sm">Export CSV</a>}
      />

      {/* Date range */}
      <Card className="mb-6 flex flex-col gap-3 p-4 sm:flex-row sm:items-end">
        <label className="flex-1">
          <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-400">From</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className={inputClass} />
        </label>
        <label className="flex-1">
          <span className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-400">To</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={inputClass} />
        </label>
        <button onClick={run} className="btn-primary">Run report</button>
      </Card>

      {loading ? (
        <Spinner />
      ) : error ? (
        <ErrorNote message={error} />
      ) : report ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label="Total Flights" value={report.totalFlights} />
            <Stat label="Flight Time" value={report.totalHours.toFixed(1)} accent="text-avio" hint="h, as entered" />
            <Stat label="Deductible" value={`${report.deductiblePercentage}%`} accent="text-emerald-300" hint={`${report.deductibleHours.toFixed(1)} hrs`} />
            <Stat label="Deductible Flights" value={report.deductibleFlights} accent="text-ember" />
          </div>

          {/* Distribution by type */}
          <Card className="p-5 sm:p-6">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-300">Hours by flight type</h2>
            <div className="space-y-3">
              {FLIGHT_TYPES.map((t) => {
                const d = report.byType[t.code] || { count: 0, hours: 0 };
                const pct = Math.max(d.hours > 0 ? 6 : 0, (d.hours / maxHours) * 100);
                return (
                  <div key={t.code} className="flex items-center gap-3">
                    <span className="hidden w-40 shrink-0 truncate text-xs text-slate-400 sm:block" title={t.label}>{t.label}</span>
                    <span className="w-8 shrink-0 text-xs font-semibold text-slate-300 sm:hidden">{t.code}</span>
                    <div className="h-6 flex-1 overflow-hidden rounded-md bg-white/5">
                      <div
                        className={`flex h-full items-center rounded-md bg-gradient-to-r ${t.bar} px-2 transition-all duration-500`}
                        style={{ width: `${pct}%` }}
                      >
                        {d.hours > 0 && <span className="text-[10px] font-semibold text-night-950">{d.hours.toFixed(1)}h</span>}
                      </div>
                    </div>
                    <span className="w-14 shrink-0 text-right text-xs text-slate-500">{d.count} flt</span>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Compliance issues */}
          <Card className="p-5 sm:p-6">
            <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-300">
              Compliance {report.issues.length > 0 && <span className="text-slate-500">({report.issues.length})</span>}
            </h2>
            {report.issues.length === 0 ? (
              <p className="text-sm text-emerald-300">✓ No compliance issues found for this period.</p>
            ) : (
              <ul className="space-y-2">
                {report.issues.map((iss, i) => (
                  <li key={i} className={`flex gap-3 rounded-lg border px-3 py-2 text-sm ${iss.type === 'error' ? 'border-red-500/30 bg-red-500/10 text-red-200' : 'border-amber-500/30 bg-amber-500/10 text-amber-200'}`}>
                    <span className="font-semibold uppercase">{iss.type}</span>
                    <span className="text-slate-300">{iss.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      ) : null}
    </div>
  );
}
